'use client'
import { useCallback, useEffect, useRef } from 'react'
import { cn } from '#app/utils/misc.tsx'

interface InteractiveGradientBlobProps {
	className?: string
	containerClassName?: string
	firstColor?: string
	secondColor?: string
	pointerColor?: string
	size?: string
	interactive?: boolean
	// Position offset - moves blobs toward bottom-right (where the dot is)
	offsetX?: string
	offsetY?: string
}

export function InteractiveGradientBlob({
	className,
	containerClassName,
	firstColor = '246, 146, 30', // #f6921e in RGB
	secondColor = '235, 0, 139', // #eb008b in RGB
	pointerColor = '255, 91, 142', // #ff5b8e in RGB
	size = '80%',
	interactive = true,
	offsetX = '0%',
	offsetY = '0%',
}: InteractiveGradientBlobProps) {
	const interactiveRef = useRef<HTMLDivElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const animationRef = useRef<number | null>(null)

	// Use refs for animation values to avoid re-renders
	const curXRef = useRef(0)
	const curYRef = useRef(0)
	const tgXRef = useRef(0)
	const tgYRef = useRef(0)

	// Continuous animation loop
	const animate = useCallback(() => {
		if (!interactiveRef.current) {
			animationRef.current = requestAnimationFrame(animate)
			return
		}

		// Smooth interpolation toward target
		curXRef.current += (tgXRef.current - curXRef.current) / 20
		curYRef.current += (tgYRef.current - curYRef.current) / 20

		// Apply transform
		interactiveRef.current.style.transform = `translate(${Math.round(curXRef.current)}px, ${Math.round(curYRef.current)}px)`

		animationRef.current = requestAnimationFrame(animate)
	}, [])

	// Start animation loop on mount
	useEffect(() => {
		if (!interactive) return

		animationRef.current = requestAnimationFrame(animate)

		return () => {
			if (animationRef.current) {
				cancelAnimationFrame(animationRef.current)
			}
		}
	}, [interactive, animate])

	// Listen to window mouse events
	useEffect(() => {
		if (!interactive) return

		const handleMouseMove = (event: MouseEvent) => {
			if (!containerRef.current) return
			const rect = containerRef.current.getBoundingClientRect()

			// Check if mouse is within container bounds
			if (
				event.clientX >= rect.left &&
				event.clientX <= rect.right &&
				event.clientY >= rect.top &&
				event.clientY <= rect.bottom
			) {
				tgXRef.current = event.clientX - rect.left - rect.width / 2
				tgYRef.current = event.clientY - rect.top - rect.height / 2
			}
		}

		window.addEventListener('mousemove', handleMouseMove)
		return () => window.removeEventListener('mousemove', handleMouseMove)
	}, [interactive])

	return (
		<div
			ref={containerRef}
			className={cn(
				'absolute inset-0 overflow-hidden',
				containerClassName,
			)}
		>
			{/* Gradients container — soft blur for the blob blend.
			    Single GPU-accelerated blur is used across browsers; the
			    `translateZ(0)` + `will-change` hints promote the layer onto
			    the compositor on Chrome (which previously fell back to a
			    main-thread SVG filter pipeline). */}
			<div
				className={cn(
					'gradients-container h-full w-full',
					'[filter:blur(40px)] [transform:translateZ(0)] [will-change:filter]',
					className,
				)}
			>
				{/* First animated blob - orange, vertical movement */}
				<div
					className="absolute opacity-100"
					style={{
						background: `radial-gradient(circle at center, rgba(${firstColor}, 0.8) 0, rgba(${firstColor}, 0) 50%) no-repeat`,
						mixBlendMode: 'hard-light',
						width: size,
						height: size,
						top: `calc(50% - ${size} / 2 + ${offsetY})`,
						left: `calc(50% - ${size} / 2 + ${offsetX})`,
						transformOrigin: 'center center',
						animation: 'moveVertical 30s ease infinite',
					}}
				/>

				{/* Second animated blob - pink, circular movement */}
				<div
					className="absolute opacity-100"
					style={{
						background: `radial-gradient(circle at center, rgba(${secondColor}, 0.8) 0, rgba(${secondColor}, 0) 50%) no-repeat`,
						mixBlendMode: 'hard-light',
						width: size,
						height: size,
						top: `calc(50% - ${size} / 2 + ${offsetY})`,
						left: `calc(50% - ${size} / 2 + ${offsetX})`,
						transformOrigin: 'calc(50% - 400px)',
						animation: 'moveInCircle 20s reverse infinite',
					}}
				/>

				{/* Third animated blob - orange, circular movement */}
				<div
					className="absolute opacity-100"
					style={{
						background: `radial-gradient(circle at center, rgba(${firstColor}, 0.8) 0, rgba(${firstColor}, 0) 50%) no-repeat`,
						mixBlendMode: 'hard-light',
						width: size,
						height: size,
						top: `calc(50% - ${size} / 2 + ${offsetY})`,
						left: `calc(50% - ${size} / 2 + ${offsetX})`,
						transformOrigin: 'calc(50% + 400px)',
						animation: 'moveInCircle 40s linear infinite',
					}}
				/>

				{/* Fourth animated blob - pink, horizontal movement */}
				<div
					className="absolute opacity-70"
					style={{
						background: `radial-gradient(circle at center, rgba(${secondColor}, 0.8) 0, rgba(${secondColor}, 0) 50%) no-repeat`,
						mixBlendMode: 'hard-light',
						width: size,
						height: size,
						top: `calc(50% - ${size} / 2 + ${offsetY})`,
						left: `calc(50% - ${size} / 2 + ${offsetX})`,
						transformOrigin: 'calc(50% - 200px)',
						animation: 'moveHorizontal 40s ease infinite',
					}}
				/>

				{/* Fifth animated blob - orange, circular movement */}
				<div
					className="absolute opacity-100"
					style={{
						background: `radial-gradient(circle at center, rgba(${firstColor}, 0.8) 0, rgba(${firstColor}, 0) 50%) no-repeat`,
						mixBlendMode: 'hard-light',
						width: size,
						height: size,
						top: `calc(50% - ${size} / 2 + ${offsetY})`,
						left: `calc(50% - ${size} / 2 + ${offsetX})`,
						transformOrigin: 'calc(50% - 800px) calc(50% + 800px)',
						animation: 'moveInCircle 20s ease infinite',
					}}
				/>

				{/* Interactive pointer-following blob */}
				{interactive && (
					<div
						ref={interactiveRef}
						className="absolute -top-1/2 -left-1/2 h-full w-full opacity-70"
						style={{
							background: `radial-gradient(circle at center, rgba(${pointerColor}, 0.8) 0, rgba(${pointerColor}, 0) 50%) no-repeat`,
							mixBlendMode: 'hard-light',
						}}
					/>
				)}
			</div>
		</div>
	)
}
